import React, { useState } from "react";
import { DeleteFilled } from "@ant-design/icons";
import { updateFileLettreMissionClient } from "../../../../data/file";
import swal from "sweetalert";
import * as Yup from "yup";
import { ModalTitle } from "../../../modals/modals.components";
import { Form, Formik } from "formik";
import { Button, Col, Input, Row, Alert } from "antd";
import FormLabel from "../../../common/Form/FormLabel";
import { getIconForFileType } from "../../../layout/menu/CustomMenu";
import FormError from "../../../common/Form/FormError";
import ModalAlert from "../../../modals/Modal";

const EditFileUploadLettreMissionSigne = ({
  handleVisibility,
  exercice,
  visible,
  fetchData,
  codeSommaire,
  date,
  heureEntree,
  tache_id,
  user_id,
  intervention_id,
  currentData, // ✅ Données existantes
}) => {
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  // ✅ Fonction pour gérer la mise à jour (avec ou sans nouveau fichier)
  const handleUpdateFile = async (values) => {
    setLoadingUpdate(true);

    try {
      const fileSizeLimit = 10 * 1024 * 1024;

      // ✅ Cas 1: Mise à jour avec nouveau fichier
      if (values.selectedFiles && values.selectedFiles.length > 0) {
        for (const file of values.selectedFiles) {
          if (file.size <= fileSizeLimit) {
            const extension = file.name.split(".").pop().toLowerCase();

            const formData = new FormData();
            formData.append("id", currentData.id); // ✅ ID pour mise à jour
            formData.append("file", file);
            formData.append("reference", values.reference);
            formData.append("libelle", values.libelle);
            formData.append("description", values.description);
            formData.append("size", file.size);
            formData.append("extension", extension);
            formData.append("exercice_id", exercice.id);
            formData.append("client_id", exercice.client_id);
            formData.append("codeSommaire", codeSommaire);
            formData.append("intervention_id", intervention_id);
            formData.append("date", date);
            formData.append("heureEntree", heureEntree);
            formData.append("tache_id", tache_id);
            formData.append("user_id", user_id);

            const response = await updateFileLettreMissionClient(formData);

            if (response.status === "success") {
              await swal("Fichier mis à jour avec succès", "", "success");
              fetchData(exercice.id);
              handleVisibility();
            } else {
              await swal(
                response.message || "Erreur lors de la mise à jour du fichier",
                "",
                "error"
              );
            }
          } else {
            await swal(
              "La taille du fichier dépasse la limite de 10MB",
              "",
              "error"
            );
          }
        }
      }
      // ✅ Cas 2: Mise à jour seulement des métadonnées (sans nouveau fichier)
      else {
        const updateData = {
          ...currentData,
          reference: values.reference,
          libelle: values.libelle,
          description: values.description,
          intervention_id: intervention_id,
          date: date,
          heureEntree: heureEntree,
          tache_id: tache_id,
          user_id: user_id,
        };

        const response = await updateFileLettreMissionClient(updateData);

        if (response.status === "success") {
          await swal("Informations mises à jour avec succès", "", "success");
          fetchData(exercice.id);
          handleVisibility();
        } else {
          await swal(
            response.message || "Erreur lors de la mise à jour",
            "",
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      await swal("Une erreur est survenue lors de la mise à jour", "", "error");
    } finally {
      setLoadingUpdate(false);
    }
  };

  // ✅ Schéma de validation pour la mise à jour
  const dataValidationScheme = Yup.object({
    libelle: Yup.string()
      .required("Le nom du fichier est obligatoire")
      .min(3, "Le nom doit contenir au moins 3 caractères"),
    reference: Yup.string()
      .required("La référence est obligatoire")
      .max(50, "La référence ne peut pas dépasser 50 caractères"),
    description: Yup.string()
      .nullable()
      .max(3000, "La description ne peut pas dépasser 3000 caractères"),
  });

  return (
    <>
      <ModalAlert
        closeModal={handleVisibility}
        show={visible}
        title={
          <ModalTitle>
            Modifier la lettre de mission{" "}
            {exercice
              ? ` de ${exercice.mission || ""} - ${exercice.annee || ""}`
              : ""}
          </ModalTitle>
        }
        footer={[]}
        modalStyles={{ top: 30 }}
        width={600}
      >
        <Formik
          initialValues={{
            selectedFiles: [], // ✅ Vide car facultatif
            reference: currentData?.reference || `${codeSommaire}-XXX`,
            libelle:
              currentData?.libelle ||
              `LETTRE MISSION ${exercice?.mission} - ${exercice?.annee}`,
            description: currentData?.description || "",
            client_id: exercice?.client_id || "",
            mission_id: exercice?.id || "",
          }}
          onSubmit={(values) => handleUpdateFile(values)}
          validationSchema={dataValidationScheme}
        >
          {({ values, handleSubmit, setFieldValue, touched, errors }) => (
            <Form>
              <Row gutter={[16, 16]}>
                <Col span={20}>
                  <FormLabel label="Nouveau fichier (facultatif)" />
                  <div className="row fv-row mb-7 fv-plugins-icon-container">
                    <div className="d-flex align-center">
                      <div className="">
                        <div className="d-flex align-items-center mb-3">
                          <input
                            type="file"
                            style={{ width: "100%" }}
                            className="form-control w-lg-400px"
                            onChange={(event) => {
                              const files = event.target.files;
                              const fileSizeLimit = 10 * 1024 * 1024;
                              const validFiles = Array.from(files).filter(
                                (file) => file.size <= fileSizeLimit
                              );
                              setFieldValue("selectedFiles", validFiles);
                            }}
                          />
                        </div>
                        <span className="form-text fs-6 text-muted">
                          Taille maximale de fichier 10MB. Laissez vide si vous
                          ne voulez pas changer le fichier.
                        </span>
                      </div>
                    </div>

                    <div className="dropzone-items">
                      {/* ✅ Afficher les nouveaux fichiers sélectionnés */}
                      {values.selectedFiles &&
                        values.selectedFiles.map((file, index) => (
                          <div key={index} className="m-6">
                            <span
                              style={{ color: "#52c41a", fontWeight: "bold" }}
                            >
                              {getIconForFileType(file.name)} {file.name}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                  {touched.selectedFiles && errors.selectedFiles && (
                    <FormError fieldName="selectedFiles" />
                  )}
                </Col>
                <Col span={4}>
                  <div className="pt-10">
                    <button
                      type="button"
                      className="dropzone-remove-all btn btn-sm btn-light-primary"
                      onClick={() => {
                        setFieldValue("selectedFiles", []);
                      }}
                    >
                      <DeleteFilled
                        style={{ color: "red", fontSize: "15px" }}
                      />
                    </button>
                  </div>
                </Col>

                <Col span={24}>
                  <FormLabel label="Référence" required />
                  <Input
                    size="large"
                    placeholder={`${codeSommaire}-XXX`}
                    name="reference"
                    value={values.reference}
                    onChange={(e) => setFieldValue("reference", e.target.value)}
                  />
                  {touched.reference && errors.reference && (
                    <FormError fieldName="reference" />
                  )}
                </Col>

                <Col span={24}>
                  <FormLabel label="Nom du fichier" required />
                  <Input
                    size="large"
                    placeholder="Le nom du fichier"
                    name="libelle"
                    value={values.libelle}
                    onChange={(e) => setFieldValue("libelle", e.target.value)}
                  />
                  {touched.libelle && errors.libelle && (
                    <FormError fieldName="libelle" />
                  )}
                </Col>

                <Col span={24}>
                  <FormLabel label="Note" />
                  <Input.TextArea
                    size="large"
                    placeholder="Notes..."
                    name="description"
                    value={values.description}
                    onChange={(e) =>
                      setFieldValue("description", e.target.value)
                    }
                    showCount
                    maxLength={3000}
                    rows={2}
                  />
                  {touched.description && errors.description && (
                    <FormError fieldName="description" />
                  )}
                </Col>

                <Col span={24}>
                  <Button
                    type="primary"
                    block
                    size="large"
                    loading={loadingUpdate}
                    disabled={loadingUpdate}
                    onClick={handleSubmit}
                  >
                    {!loadingUpdate && (
                      <span className="indicator-label">
                        {values.selectedFiles && values.selectedFiles.length > 0
                          ? "Mettre à jour le fichier"
                          : "Mettre à jour les informations"}
                      </span>
                    )}
                    {loadingUpdate && (
                      <span className="indicator-progress text-white">
                        Mise à jour en cours...
                        <span className="spinner-border spinner-border-sm align-middle ms-2 text-white"></span>
                      </span>
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </ModalAlert>
    </>
  );
};

export default EditFileUploadLettreMissionSigne;
